import type { TransformerExtended } from "@/Common/Models/Domain/Transformer";

import type { UserApi } from "@/Modules/Models/Api/User";
import type { User } from "@/Modules/Models/Domain/User";

import DefaultTransformerGenerator from "@/Common/Transformers/FunctionalBase";
import TranscriptionTransformer, {
  type TranscriptionTransformerType,
} from "@/Modules/Transformers/User/Transcription";

import { hasValue } from "@/Utils/Objects";

const UserTransformer: TransformerExtended<User, UserApi> = {
  ...DefaultTransformerGenerator<User, UserApi>(),

  toDomain(userApi: UserApi) {
    const transcriptionTransformer: TranscriptionTransformerType = TranscriptionTransformer;
    const transcriptionDomain = hasValue(userApi.transcription)
      ? transcriptionTransformer.toDomain(userApi.transcription)
      : null;

    const user: User = {
      avatar: userApi.avatar,
      description: userApi.description,
      email: userApi.email,
      id: userApi.uuid,
      b2bChangeEmail: userApi.b2b_change_email,
      isB2b: userApi.is_b2b,
      isCouple: userApi.is_couple,
      isUnder18: userApi.is_under_18,
      isGuest: userApi.is_guest === true,
      isWantTrial: userApi.is_want_trial,
      isNotificationActive: userApi.push_alert,
      isEmailValidated: userApi.is_email_validated,
      language: userApi.language,
      lastSubscription: userApi.last_subscription,
      promoCode: userApi.promo_code,
      patientId: null,
      referralCode: userApi.referral,
      role: userApi.role,
      status: userApi.status,
      therapistId: userApi.therapist,
      username: userApi.username,
      gender: userApi.gender,
      ageRange: userApi.age_range,
      country: userApi.country,
      area: userApi.area,
      availability: userApi.availability,
      customerUuid: userApi.customer_uuid,
      referralDiscountPercentage: userApi.referral_discount_percentage,
      isIntegration: userApi.is_integration,
      therapistShow: userApi.therapist_show,
      isBreakable: userApi.is_breakable,
      isAvailable: userApi.is_available,
      isCustomerComeback: userApi.is_customer_comeback,
      isReassigned: userApi.is_reassigned,
      isSameTherapist: !!userApi.is_same_therapist,
      notificationBannerMessage: userApi.notification_banner_message,
      sessionInCurrentMonth: userApi.session_in_current_month,
      isWellhub: userApi.is_wellhub,
      isGympass: userApi.is_gympass,
      isCustomerSSO: userApi.is_customer_sso,
      transcription: transcriptionDomain,
      isTranscriptionEnableRequested: userApi.is_transcription_enable_requested,
      isCustomerReassign: userApi.is_customer_reassign,
      timeZone: userApi.time_zone,
      canBeDischarged: userApi.can_be_discharged,
      hasExtraPlans: userApi.has_extra_plans,
      isPromo: userApi.is_promo,
      isAgentInMaintenance: userApi.is_agent_in_maintenance ?? true,
      isChangeEmailRequired: userApi.user_must_change_email,
    };

    if (userApi.last_missing_email_notification) {
      user.lastMissingEmailNotification = userApi.last_missing_email_notification;
    }

    if (userApi.therapist === "") {
      user.therapistId = null;
    }

    return user;
  },
};

export type UserTransformerType = TransformerExtended<User, UserApi>;

export default UserTransformer;
